"use client";

import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";

interface ComboboxProps {
	placeholder?: string;
	options: { value: string; label: string }[];
	selected: string[];
	onChange: (selected: string[]) => void;
	className?: string;
}

export function Combobox({
	options,
	placeholder = "Select option...",
	selected,
	onChange,
	className,
}: ComboboxProps) {
	const [open, setOpen] = React.useState(false);

	const handleSelect = React.useCallback(
		(value: string) => {
			const updatedSelected = selected.includes(value)
				? selected.filter((item) => item !== value)
				: [...selected, value];
			onChange(updatedSelected);
		},
		[selected, onChange],
	);

	const selectedLabels = React.useMemo(
		() =>
			selected
				.map((value) => options.find((option) => option.value === value)?.label)
				.filter(Boolean)
				.join(", "),
		[selected, options],
	);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					// role="combobox"
					aria-expanded={open}
					className={cn("max-w-[100px] justify-between", className)}
				>
					<span className="truncate">
						{selected.length > 0 ? selectedLabels : placeholder}
					</span>
					<ChevronsUpDown className="opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="min-w-[200px] p-0">
				<Command>
					<CommandInput placeholder={placeholder} className="h-9" />
					<CommandList>
						<CommandEmpty>Not found.</CommandEmpty>
						<CommandGroup>
							{options.map((option) => (
								<CommandItem
									key={option.value}
									value={option.value}
									onSelect={(currentValue) => {
										handleSelect(currentValue);
										setOpen(false);
									}}
									className="text-primary"
								>
									{option.label}
									<Check
										className={cn(
											"ml-auto h-4 w-4",
											selected.includes(option.value)
												? "opacity-100"
												: "opacity-0",
										)}
									/>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
